import { PDF_TYPES } from "../../utils";

import mock_data_team_profile from "./team-profile.json";
import mock_data_team_profile_culture_long from "./team-profile-culture-long.json";
import mock_data_team_profile_culture_long_large from "./team-profile-culture-long-large.json";
import mock_data_matching_profile from "./matching-profile.json";
import mock_data_matching_profile_culture_short from "./matching-profile-culture-short.json";
import mock_data_talent_profile from "./talent-profile.json";
import mock_data_talent_profile_culture_long from "./talent-profile-culture-long.json";
import mock_data_requirement_profile from "./requirement-profile.json";

/* Alt-Payload-Pfad des Anforderungsprofils: ohne top_dimensions rendert die
   Top-3-Bande nicht — und die Rangliste zeigt dann wieder alle 10 Zeilen ab 01
   statt ab 04. Seit die Bande steuert, welche Zeilen die Liste weglaesst, ist
   dieser Pfad verhaltenstragend und gehoert in die Vorschau.

   Als Ableitung und nicht als eigene JSON-Datei wie die uebrigen Varianten: die
   unterscheiden sich in einem ganzen Block (long vs. short mode), diese hier in
   der Abwesenheit eines einzigen Feldes. Eine zweite 900-Zeilen-Kopie wuerde
   genau daneben driften. */
const mock_data_requirement_profile_no_top = {
  ...mock_data_requirement_profile,
  culture_section: { ...mock_data_requirement_profile.culture_section },
};
delete mock_data_requirement_profile_no_top.culture_section.top_dimensions;

/* getComponentPlacement setzt den Hinweis-Block auf die erste verfuegbare
   Kategorie, Reihenfolge Interessen -> Kultur -> Persoenlichkeit -> Skills. Mit
   Interessen im Payload steht er nie auf der Werte-Seite — der gemeldete
   Umbruch (Ueberschrift allein am Seitenfuss, Rumpf auf der Folgeseite) war
   lokal deshalb gar nicht reproduzierbar. Diese Variante schiebt ihn dorthin.

   Wie beim Anforderungsprofil eine Ableitung statt einer zweiten 900-Zeilen-
   Kopie: der Unterschied ist ein einziges leeres Feld, eine eigene Datei wuerde
   genau daneben driften. */
const mock_data_talent_profile_culture_long_hint = {
  ...mock_data_talent_profile_culture_long,
  interests_section: {
    ...mock_data_talent_profile_culture_long.interests_section,
    data: [],
  },
};

/* Key-Insights-Varianten. Der Vertrag erlaubt 1-3 Bullets (README), jede
   eingecheckte Fixture liefert aber drei — der gemeldete Bug (die groesste
   Abweichung nach einer Zeile mit "…" abgeschnitten) war in der Vorschau
   deshalb ueberhaupt nicht sichtbar. Wie oben Ableitungen statt eigener
   Dateien: der Unterschied sind zwei Array-Eintraege. bullets wird NEU gebaut
   und nicht mutiert — das JSON-Modul ist geteilt. */
const withKeyInsightBullets = (bullets) => ({
  ...mock_data_matching_profile,
  cultural_fit: {
    ...mock_data_matching_profile.cultural_fit,
    key_insights: {
      ...mock_data_matching_profile.cultural_fit.key_insights,
      bullets,
    },
  },
});

/* Der Kundenfall wortgleich: hits = 0, deshalb liefert das Backend kein
   "Tragende Gemeinsamkeit"-Bullet und die Abweichung sitzt auf Index 1. Ihr
   Satz misst 1212.5pt (167 Zeichen, Fira Sans 400 @16pt) und braucht damit
   zwei Zeilen; das Budget gibt ihm bei zwei Bullets zwei. Gemeldet wurde er in
   der alten, unsymmetrischen Fassung ("beim Talent auf Rang 10"). */
const KEY_INSIGHT_BULLETS_TWO = [
  {
    label: "Passung",
    text: "0 von 3 Kernwerten der Stelle geteilt · insgesamt 3 von 10 Werten relativ nah beieinander",
  },
  {
    label: "Größte Abweichung",
    text: "Beständigkeit & Tradition – der Stelle wichtig (Rang 1), dem Talent weniger wichtig (Rang 10) → Hintergründe etablierter Abläufe erklären statt nur die Regel zu nennen",
  },
];

const mock_data_matching_profile_insights_two = withKeyInsightBullets(
  KEY_INSIGHT_BULLETS_TWO,
);

/* Backend-Kontrakt gebrochen: der dritte Mock-Text plus Zusatz misst 2480.7pt
   (331 Zeichen) und braucht damit auch bei 1145pt Messbreite drei Zeilen. Bei
   drei Bullets hat das letzte zwei — die Variante prueft also die Bremse selbst
   statt ihrer Abwesenheit: die Ellipse muss das dritte Bullet treffen (nicht
   ein kurzes davor) und die Karte trotzdem auf der Kulturseite bleiben.
   Absichtlich drei Bullets: bei zwei bekaeme der lange Satz drei Zeilen und
   stuende vollstaendig da, dann testet die Fixture nichts. */
const mock_data_matching_profile_insights_overflow = withKeyInsightBullets([
  ...mock_data_matching_profile.cultural_fit.key_insights.bullets.slice(0, 2),
  {
    ...mock_data_matching_profile.cultural_fit.key_insights.bullets[2],
    text: `${mock_data_matching_profile.cultural_fit.key_insights.bullets[2].text} und im Onboarding gemeinsam festlegen, welche Routinen bewusst bleiben und welche zur Diskussion stehen, damit die Erwartungen auf beiden Seiten von Anfang an klar sind`,
  },
]);

/* Der gemeldete Interessen-Fehler: beide Spaltentitel der Karte standen als
   fettes "undefined" da. Ursache ist nicht die Karte, sondern der Payload —
   die Textgenerierung liefert inzwischen einen generalTranslations-Satz, in
   dem riasec_top_three_title neu formuliert ist und riasec_career_title /
   riasec_talent_title gar nicht mehr vorkommen. Die eingecheckten
   Matching-Fixtures tragen noch den alten Satz, deshalb war der Fehler lokal
   nicht sichtbar.

   Wie die Varianten oben eine Ableitung und keine zweite 1800-Zeilen-Kopie:
   der Unterschied sind drei Felder in generalTranslations. */
const withProductionRiasecTranslations = (data) => {
  const generalTranslations = {
    ...data.generalTranslations,
    riasec_top_three_title:
      "Vergleich von Personeninteressen und Aufgabeninhalten der Stelle",
  };
  delete generalTranslations.riasec_career_title;
  delete generalTranslations.riasec_talent_title;

  return { ...data, generalTranslations };
};

const mock_data_matching_profile_riasec_no_titles =
  withProductionRiasecTranslations(mock_data_matching_profile);

/* Dieselbe Luecke auf Englisch. Pruefstueck ist die lokale Kopie in
   RiasecInsights: Untertitel, Rang-Label und der Ring-Eintrag der Legende
   standen dort hart in Deutsch und blieben damit auch in einem englischen
   Report deutsch.
   Der Kartenkopf bleibt hier deutsch, und das ist richtig so: er kommt aus
   generalTranslations, und die liegen im Mock nur in EINER Sprache vor (siehe
   README, Teil 4) — in Produktion kommen sie sprachfertig aus dem Backend. */
const mock_data_matching_profile_riasec_no_titles_en = {
  ...mock_data_matching_profile_riasec_no_titles,
  language: "en",
};

export const mockData = {
  REQUIREMENTPROFILE: mock_data_requirement_profile,
  MATCHINGPROFILE: mock_data_matching_profile,
  TALENTPROFILE: mock_data_talent_profile,
  TEAMPROFILE: mock_data_team_profile,
};

/**
 * Varianten, in der Dev-Vorschau über ?mock=<key> wählbar. Der Key trägt den
 * PDF-Typ mit, damit auch ein anderer Typ als der Default erreichbar ist,
 * ohne src/index.js anzufassen.
 *
 * Culture hat zwei Assessment-Modi, die unterschiedliche Komponenten rendern:
 * "long" (Werte-Rangliste aus der neuen CULTURE-Challenge) und "short"
 * (4 Quadranten aus HerosJourney). Die Fixtures eines Typs unterscheiden sich
 * ausschließlich im Culture-Block.
 */
export const mockDataVariants = {
  "culture-long": {
    type: PDF_TYPES.MATCHING_PROFILE,
    data: mock_data_matching_profile,
  },
  "culture-short": {
    type: PDF_TYPES.MATCHING_PROFILE,
    data: mock_data_matching_profile_culture_short,
  },
  /* nur zwei Bullets: die groesste Abweichung steht auf Index 1 und muss
     trotzdem vollstaendig stehen */
  "culture-long-insights-two": {
    type: PDF_TYPES.MATCHING_PROFILE,
    data: mock_data_matching_profile_insights_two,
  },
  /* ueberlanges letztes Bullet: die Ellipse ist hier Absicht, die Karte bleibt
     auf der Kulturseite */
  "culture-long-insights-overflow": {
    type: PDF_TYPES.MATCHING_PROFILE,
    data: mock_data_matching_profile_insights_overflow,
  },
  /* Produktions-Payload der Interessen-Seite: ohne riasec_career_title und
     riasec_talent_title. Beide Spaltentitel muessen trotzdem stehen. */
  "riasec-no-titles": {
    type: PDF_TYPES.MATCHING_PROFILE,
    data: mock_data_matching_profile_riasec_no_titles,
  },
  /* dasselbe auf Englisch: prueft die lokale Kopie der Karte */
  "riasec-no-titles-en": {
    type: PDF_TYPES.MATCHING_PROFILE,
    data: mock_data_matching_profile_riasec_no_titles_en,
  },
  "talent-culture-long": {
    type: PDF_TYPES.TALENT_PROFILE,
    data: mock_data_talent_profile_culture_long,
  },
  "talent-culture-short": {
    type: PDF_TYPES.TALENT_PROFILE,
    data: mock_data_talent_profile,
  },
  /* ohne Interessen: der Hinweis-Block landet auf der Werte-Seite und zeigt,
     ob er dort komplett steht */
  "talent-culture-long-hint": {
    type: PDF_TYPES.TALENT_PROFILE,
    data: mock_data_talent_profile_culture_long_hint,
  },
  "team-culture-long": {
    type: PDF_TYPES.TEAM_PROFILE,
    data: mock_data_team_profile_culture_long,
  },
  /* 12 Mitglieder, davon 11 mit Werte-Daten: prueft die Kollisionsstrategie
     (Zaehler-Ringe) und dataset_not_complete */
  "team-culture-long-large": {
    type: PDF_TYPES.TEAM_PROFILE,
    data: mock_data_team_profile_culture_long_large,
  },
  "team-culture-short": {
    type: PDF_TYPES.TEAM_PROFILE,
    data: mock_data_team_profile,
  },
  "requirement-culture-long": {
    type: PDF_TYPES.REQUIREMENT_PROFILE,
    data: mock_data_requirement_profile,
  },
  "requirement-culture-long-no-top": {
    type: PDF_TYPES.REQUIREMENT_PROFILE,
    data: mock_data_requirement_profile_no_top,
  },
};
